import React from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { getBusinessLocation, getGoogleMapsUrl, buildGooglePlaceUrl } from "../../../services/helpers/BusinessLocationHelper";
import { useBusinessGooglePlace } from "../../../hooks/useBusinessGooglePlace";
import "./styles/BusinessGoogleMapsBadge.css";

interface BusinessGoogleMapsBadgeProps {
  businessId: string;
  location: unknown;
}

const BusinessGoogleMapsBadge: React.FC<BusinessGoogleMapsBadgeProps> = ({ businessId, location }) => {
  const { mapsUrl, placeId } = getBusinessLocation(location);
  const href = getGoogleMapsUrl(mapsUrl) ?? (placeId ? buildGooglePlaceUrl(placeId) : null);
  const place = useBusinessGooglePlace(businessId, href ? placeId : undefined);
  if (!href) return null;

  const hasRating = place?.rating != null && place.userRatingCount != null && place.userRatingCount > 0;
  const ratingLabel = hasRating ? `, rated ${place!.rating!.toFixed(1)} out of 5 from ${place!.userRatingCount} ratings` : "";

  const hasAttributions = hasRating && place!.attributions && place!.attributions.length > 0;

  return (
    <div className="biz-contact-stat">
      <a
        className="biz-contact-stat__link"
        href={place?.googleMapsUri ?? href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open business on Google Maps${ratingLabel}`}
      >
        <div className="biz-contact-stat__top" style={{ color: '#16a34a' }}>
          <img src="/google-maps-pin.svg" alt="" width={12} height={16} aria-hidden="true" /> Google Maps rating
        </div>
        <div className="biz-contact-stat__value">
          {hasRating ? (
            <>
              {place!.rating!.toFixed(1)} <span className="biz-contact-stat__stars">★★★★★</span>
            </>
          ) : "Open"}
        </div>
        <div className="biz-contact-stat__bottom">
          {hasRating ? (
            <>{place!.userRatingCount!.toLocaleString()} reviews · Open in Maps <ArrowUpRight size={12} style={{ marginLeft: 2 }} /></>
          ) : (
            <>Open in Maps <ArrowUpRight size={12} style={{ marginLeft: 2 }} /></>
          )}
        </div>
      </a>
      {hasAttributions && (
        <div className="biz-google-maps__providers">
          {place!.attributions.map(({ provider, providerUri }, index) =>
            providerUri ? (
              <a key={`${provider}-${index}`} href={providerUri} target="_blank" rel="noopener noreferrer">
                {provider}
              </a>
            ) : (
              <span key={`${provider}-${index}`}>{provider}</span>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessGoogleMapsBadge;
